import { useCallback, useMemo } from 'react'
import {
    isColorOption,
    isOptionAvailable,
    findVariantByOptions,
    buildOptionKeyMap,
    type VLPOption,
    type VLPVariant,
} from '@storefronts/common-types'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { VariantGroup } from './components/VariantGroup'
import { useVariantSelection } from './hooks/useVariantSelection'

export type ManualDefaultVariantSelectorProps = {
    variants: VLPVariant[]
    options: VLPOption[]
    defaultVariantId: string | null
    onVariantSelect: (variantId: string) => void
    showVariantImagesAsColors: boolean
    /** No-op default; not used when `showVariantImagesAsColorsToggle` is false. */
    onShowVariantImagesAsColorsChange?: (checked: boolean) => void
    /** Distinct id for the color-swatch toggle (a11y). */
    colorSwatchSwitchId?: string
    /** Prepended to VariantGroup React keys when multiple pickers mount. */
    reactKeysPrefix?: string
    /**
     * When false, hides the “variant images as swatches” switch (value still
     * drives option rows). Use where the setting is not persisted, e.g. storefront
     * override in the link picker.
     */
    showVariantImagesAsColorsToggle?: boolean
}

/**
 * Option rows + optional “variant images as color swatches” toggle for picking
 * a single default variant. Used by {@link DefaultVariantSelector} (manual mode)
 * and storefront override UI.
 */
export const ManualDefaultVariantSelector: React.FC<
    ManualDefaultVariantSelectorProps
> = ({
    variants,
    options,
    defaultVariantId,
    onVariantSelect,
    showVariantImagesAsColors,
    onShowVariantImagesAsColorsChange = () => {},
    colorSwatchSwitchId = 'manual-default-variant-show-images',
    reactKeysPrefix = '',
    showVariantImagesAsColorsToggle = true,
}) => {
    const { selectedOptions, handleOptionSelect } = useVariantSelection({
        variants,
        options,
        defaultVariantId,
        onVariantSelect,
    })

    const optionKeyMap = useMemo(() => buildOptionKeyMap(options), [options])

    const onImageToggle = useCallback(
        (checked: boolean) => {
            onShowVariantImagesAsColorsChange(checked)
        },
        [onShowVariantImagesAsColorsChange],
    )

    const hasColorOptions = options.some(isColorOption)

    if (variants.length <= 1) return null

    const keyPrefix = reactKeysPrefix ? `${reactKeysPrefix}-` : ''

    return (
        <div className="flex flex-col gap-4">
            {showVariantImagesAsColorsToggle && hasColorOptions && (
                <div>
                    <div className="mb-4 flex items-center">
                        <Label
                            htmlFor={colorSwatchSwitchId}
                            className="mb-0 mr-4"
                        >
                            Use product variant images as missing color swatches
                        </Label>
                        <Switch
                            id={colorSwatchSwitchId}
                            checked={showVariantImagesAsColors}
                            onCheckedChange={onImageToggle}
                        />
                    </div>
                </div>
            )}
            {options.map((option, idx) => {
                if (option.values.length === 1) return null
                return (
                    <VariantGroup
                        key={`${keyPrefix}${option.name}-${idx}`}
                        option={option}
                        currentSelection={selectedOptions.find(
                            (o) => o.name === option.name,
                        )}
                        isAvailable={(value: string) =>
                            isOptionAvailable({
                                variants,
                                optionName: option.name,
                                optionValue: value,
                                selectedOptions,
                                optionKeyMap,
                            })
                        }
                        onSelect={(value: string) =>
                            handleOptionSelect(option.name, value)
                        }
                        getMatchingVariant={(value: string) =>
                            findVariantByOptions(
                                variants,
                                [{ name: option.name, value }],
                                optionKeyMap,
                            )
                        }
                        showVariantImagesAsColors={showVariantImagesAsColors}
                    />
                )
            })}
        </div>
    )
}
